/**
 * Helpers for safely constructing redirect URLs.
 *
 * Open-redirect hardening: any "where to go next" string that comes
 * from a query param (e.g. Login's `?next=`, RootShell's location)
 * MUST go through `safeRedirectPath` before being passed to
 * `navigate()`. Naive `startsWith("/")` accepts protocol-relative URLs
 * like `//evil.com/path` and backslash-prefixed paths like
 * `/\evil.com` that some routers normalise into cross-origin
 * navigations. We pin the allow-list to "starts with `/`, but not `//`
 * and not `/\`" so the destination is always same-origin.
 */
export function safeRedirectPath(
  next: string | null | undefined,
): string | null {
  if (!next) return null;
  if (!next.startsWith("/")) return null;
  if (next.startsWith("//")) return null;
  if (next.startsWith("/\\")) return null;
  return next;
}

/**
 * Build the /login URL with an optional `next` query param so the user
 * lands back on the page that 401'd. Skip the param when the source is
 * itself a /login page (otherwise we'd build /login?next=/login which
 * is a useless redirect loop).
 */
export function buildLoginRedirect(here: string): string {
  if (
    here === "/login" ||
    here.startsWith("/login?") ||
    here.startsWith("/login#")
  ) {
    return "/login";
  }
  const safe = safeRedirectPath(here);
  if (!safe) return "/login";
  return `/login?next=${encodeURIComponent(safe)}`;
}

/**
 * Pages a signed-out visitor is supposed to be on. A 401 from the
 * mount-time `GET /me` there is expected, not a revoked session, so
 * the global `auth-revoked` listener must not navigate away — on
 * `/reset-password` that would also discard the token held from the
 * URL fragment (spec 0009 §5.8).
 */
const PUBLIC_AUTH_PATHS = new Set(["/login", "/forgot-password", "/reset-password"]);

export function isPublicAuthPath(pathname: string): boolean {
  // Normalise the way React Router matches: case-insensitive, trailing
  // slashes ignored. Otherwise `/reset-password/` renders the page but
  // isn't exempt, and the 401 bounce drops the token.
  const normalised = pathname.toLowerCase().replace(/\/+$/, "") || "/";
  return PUBLIC_AUTH_PATHS.has(normalised);
}
